// Recursive statement-level function call expander.
// Expands known calls and preserves unmatched statements as-is.
import { expandInlineFunctionCalls } from './inline'
import { splitLines, splitTopLevelStatements } from '../../variables/class-variable/transform/lines'
import { parseFunctionCallStatement } from './parse'
import type { LuisFunctionExpansionResult, LuisFunctionMap } from '../types'
import { expandFunctionCall } from './expand-call'
import { MAX_FUNCTION_EXPANSION_DEPTH, type ExpansionContext } from './expand-types'
import { parseLegacyCallName, parseLegacyPrefixedCallName } from './legacy'
import { pushExpansionWarning } from './warnings'

// Build function-call expansion output from extracted definitions.
export function expandFunctionCalls(source: string, functions: LuisFunctionMap, collectWarnings = true): LuisFunctionExpansionResult {
  const context: ExpansionContext = { _functions: functions, _warnings: [], _collectWarnings: collectWarnings }
  const expandedSource = expandFunctionCallsInternal(source, context, [], 0, 1)
  // Return expanded source and collected expansion warnings.
  return { source: expandedSource, warnings: context._warnings }
}

// Build recursive function-call expansion while preserving non-call statements.
function expandFunctionCallsInternal(
  source: string,
  context: ExpansionContext,
  stack: string[],
  depth: number,
  baseLine: number,
): string {
  // Check whether recursion depth reached the safety limit.
  if (depth > MAX_FUNCTION_EXPANSION_DEPTH) {
    // Return source unchanged when recursion depth is too high.
    return source
  } else {
    // Continue recursive function-call expansion.
  }

  const lines = splitLines(source)
  const outputLines: string[] = []
  // Expand each line statement-by-statement for deterministic output.
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex]
    const lineNumber = baseLine + lineIndex
    const expandedLine = expandLineCalls(line, lineNumber, context, stack, depth)
    outputLines.push(expandInlineFunctionCalls(expandedLine, lineNumber, context, stack, depth, expandFunctionCallsInternal))
  }

  // Return source text with expanded function calls.
  return outputLines.join('\n')
}

// Build one source line with statement-level function-call expansion.
function expandLineCalls(
  line: string,
  lineNumber: number,
  context: ExpansionContext,
  stack: string[],
  depth: number,
): string {
  const parts = splitTopLevelStatements(line)
  const transformedParts: string[] = []
  let changed = false

  // Start one semicolon-delimited statement expansion pass.
  for (const part of parts) {
    const call = part.hasTerminator ? parseFunctionCallStatement(part.text) : null
    if (!call) {
      const legacyName = part.hasTerminator ? parseLegacyCallName(part.text) : null
      const legacyPrefixedName = part.hasTerminator ? parseLegacyPrefixedCallName(part.text) : null
      if (legacyName && legacyName in context._functions) {
        // Warn for legacy bare function-call syntax.
        pushExpansionWarning(
          context,
          'invalid-function-call',
          lineNumber,
          `Legacy function call "${legacyName}(...)" is not supported. Use "@fun.${legacyName}(...)".`,
        )
      } else if (legacyPrefixedName && legacyPrefixedName in context._functions) {
        // Warn for legacy whitespace-prefixed @fun call syntax.
        pushExpansionWarning(
          context,
          'invalid-function-call',
          lineNumber,
          `Legacy function call "@fun ${legacyPrefixedName}(...)" is not supported. Use "@fun.${legacyPrefixedName}(...)".`,
        )
      } else {
        // Continue when statement is not a legacy function call.
      }
      const suffix = part.hasTerminator ? ';' : ''
      transformedParts.push(`${part.text}${suffix}`)
      continue
    } else {
      // Continue expansion for parsed function call statements.
    }

    const definition = context._functions[call.name]
    if (!definition) {
      // Warn when current statement references an unknown function.
      pushExpansionWarning(context, 'unknown-function-call', lineNumber, `Function "${call.name}" is not defined.`)
      transformedParts.push(`${part.text};`)
      continue
    } else {
      // Continue expansion for known function definition.
    }

    transformedParts.push(expandFunctionCall(definition, call, context, stack, depth, lineNumber, expandFunctionCallsInternal))
    changed = true
  }

  // Keep original line unchanged when no statement was expanded.
  if (!changed) {
    // Return the original line when no statement changed.
    return line
  } else {
    // Return line with expanded statement output.
    return transformedParts.join('\n')
  }
}
